import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { authApi } from "../api/endpoints";
import { AuthLayout } from "../components/Layout";
import { Alert, Button, LoadingBlock, TextField } from "../components/ui";
import { useDocumentTitle, useMutation } from "../hooks/useApi";
import { useSession } from "../hooks/SessionContext";

export function VerifyEmailPage() {
  useDocumentTitle("Vérification de l'adresse e-mail");
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refresh, state } = useSession();
  const [status, setStatus] = useState<"idle" | "pending" | "done" | "failed">(
    token ? "pending" : "idle",
  );
  const [message, setMessage] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    void (async () => {
      try {
        const response = await authApi.verifyEmail(token);
        setMessage(response.message);
        setStatus("done");
        // Si l'utilisateur est déjà connecté, l'état doit refléter la
        // vérification sans qu'il ait à se reconnecter.
        await refresh();
      } catch (error) {
        setMessage(
          error instanceof Error
            ? error.message
            : "Ce lien de vérification n'a pas pu être utilisé.",
        );
        setStatus("failed");
      }
    })();
  }, [token, refresh]);

  const [email, setEmail] = useState("");
  const [resentMessage, setResentMessage] = useState<string | null>(null);
  const resend = useMutation(() => authApi.resendVerification(email.trim()), {
    // Le message du serveur est volontairement neutre : il ne révèle pas si un
    // compte existe pour cette adresse.
    onSuccess: (response) => setResentMessage(response.message),
  });

  if (status === "pending") {
    return (
      <AuthLayout title="Vérification en cours">
        <LoadingBlock label="Vérification de votre adresse…" />
      </AuthLayout>
    );
  }

  if (status === "done") {
    return (
      <AuthLayout
        title="Adresse vérifiée"
        footer={
          <Link to={state?.authenticated ? "/" : "/connexion"}>
            {state?.authenticated ? "Aller au tableau de bord" : "Se connecter"}
          </Link>
        }
      >
        <Alert tone="success" title="C'est fait">
          {message ?? "Votre adresse e-mail est vérifiée."}
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Vérifier votre adresse e-mail"
      intro={
        status === "failed"
          ? "Ce lien n'est plus valide. Demandez un nouvel envoi ci-dessous."
          : "Saisissez votre adresse pour recevoir un nouveau lien de vérification."
      }
      footer={<Link to="/connexion">Retour à la connexion</Link>}
    >
      {status === "failed" && message && (
        <Alert tone="critical" title="Lien inutilisable">
          {message}
        </Alert>
      )}
      {resend.error && (
        <Alert tone="critical" title="Envoi impossible">
          {resend.error.message}
        </Alert>
      )}
      {resentMessage && (
        <Alert tone="success" title="Demande enregistrée">
          {resentMessage}
        </Alert>
      )}

      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          void resend.run();
        }}
        noValidate
      >
        <TextField
          label="Adresse e-mail"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={resend.fieldErrors.email}
        />
        <Button type="submit" variant="primary" block loading={resend.pending}>
          Renvoyer le lien de vérification
        </Button>
      </form>
    </AuthLayout>
  );
}
